import type {
  CreateSurgeonsPayload,
  MergeSurgeonsPayload,
  Surgeon,
  SurgeonsRequestFilters,
  SurgeonsResponse,
  UnrecognisedSurgeon,
  UnrecognisedSurgeonMergeResponse,
  UpdateSurgeonPayload,
} from 'api/models/Surgeons';
import ApiClientBase from './ApiClientBase';
import formatSurgeonName from 'utils/formatSurgeonName';

// map the surgeon name according to the title firstName lastName rules
const withNameFormatted = (surgeon: Surgeon): Surgeon => ({
  ...surgeon,
  nameFormatted: formatSurgeonName(surgeon.title, surgeon.firstName, surgeon.lastName) || surgeon.name,
});

class SurgeonsEndpoint extends ApiClientBase {
  apiUri = '/api/v2/hospitals/surgeons';

  async getSurgeons(params: SurgeonsRequestFilters) {
    // Prepare a new object with all values converted to strings
    // and remove the if they are empty
    if (!params.limit) params.limit = '-1';
    const qs = new URLSearchParams(
      Object.entries(params).flatMap(([key, value]) =>
        value !== undefined && value !== '' ? [[key, String(value)]] : [],
      ),
    );

    // Make the API call with the generated query string
    const surgeons = await this.apiCall<SurgeonsResponse>({ uri: '/', data: qs });

    surgeons.surgeons = surgeons.surgeons.map(withNameFormatted);
    return surgeons;
  }

  /** validated surgeons at one hospital, for internal users picking a merge target */
  async getHospitalSurgeons(hospitalLocationId: string) {
    const surgeons = await this.apiCall<Surgeon[]>({
      apiUri: '/api/v2/hospitals',
      uri: `/${hospitalLocationId}/surgeons/`,
    });
    return surgeons.map(withNameFormatted);
  }

  getUnrecognisedSurgeons() {
    return this.apiCall<UnrecognisedSurgeon[]>({ apiUri: '/api/v2/surgeons', uri: '/unrecognised/' });
  }

  mergeUnrecognisedSurgeon(surgeonId: string, targetSurgeonId: string) {
    return this.apiCall<UnrecognisedSurgeonMergeResponse>({
      apiUri: '/api/v2/surgeons',
      uri: `/unrecognised/${surgeonId}/merge/`,
      method: 'put',
      data: { targetSurgeonId },
    });
  }

  confirmUnrecognisedSurgeonDistinct(surgeonId: string) {
    return this.apiCall<void>({
      apiUri: '/api/v2/surgeons',
      uri: `/unrecognised/${surgeonId}/confirm_distinct/`,
      method: 'put',
    });
  }

  createSurgeons(payload: CreateSurgeonsPayload) {
    return this.apiCall(`/`, 'post', payload);
  }

  updateSurgeon(surgeonId: Surgeon['id'], payload: UpdateSurgeonPayload) {
    return this.apiCall(`/${surgeonId}/`, 'put', payload);
  }

  mergeSurgeons(payload: MergeSurgeonsPayload) {
    return this.apiCall({
      apiUri: '/api/v2/surgeons',
      uri: '/migrate_surgeon_data/',
      method: 'put',
      data: payload,
    });
  }

  addSurgeonToHospital(surgeonId: Surgeon['id']) {
    return this.apiCall(`/${surgeonId}/`, 'post');
  }
}

const surgeons = new SurgeonsEndpoint();
export default surgeons;
